/**
 * Medsheet Service - Drug-to-sheet mapping and lookup
 * 
 * Provides functionality to:
 * - Look up which medsheets contain a given drug
 * - Handle brand/generic name variations
 * - Detect name differences for user notification
 */

// Sheet titles for display (human-readable names)
export const SHEET_TITLES = {
  'antiarrhythmics.html': 'Antiarrhythmics',
  'antidotes-reversal.html': 'Antidotes & Reversal',
  'antihypertensive-dosing.html': 'Antihypertensive Dosing',
  'emergency-med-dosing.html': 'Emergency Med Dosing',
  'empiric-antibiotics.html': 'Empiric Antibiotics',
  'iv-pressors-dosing.html': 'IV Pressors Dosing',
  'procedural-sedation.html': 'Procedural Sedation',
  'status-epilepticus.html': 'Status Epilepticus',
  'thrombolytics-anticoag.html': 'Thrombolytics & Anticoag',
  'toxicology-protocols.html': 'Toxicology Protocols',
};

// Drug class/category to representative drug mapping
// When AI returns generic terms like "beta-blocker", we map to relevant drugs
// Format: normalized category name -> { displayName, drugs: [drug names to look up] }
const DRUG_CATEGORIES = {
  // Beta-blockers
  'betablocker': { displayName: 'Beta-blockers', drugs: ['metoprolol', 'esmolol', 'propranolol', 'labetalol', 'carvedilol', 'sotalol'] },
  'betablockers': { displayName: 'Beta-blockers', drugs: ['metoprolol', 'esmolol', 'propranolol', 'labetalol', 'carvedilol', 'sotalol'] },
  'bblocker': { displayName: 'Beta-blockers', drugs: ['metoprolol', 'esmolol', 'propranolol', 'labetalol', 'carvedilol', 'sotalol'] },
  
  // Calcium channel blockers
  'calciumchannelblocker': { displayName: 'Calcium Channel Blockers', drugs: ['diltiazem', 'verapamil', 'nicardipine', 'clevidipine', 'amlodipine', 'nifedipine'] },
  'ccb': { displayName: 'Calcium Channel Blockers', drugs: ['diltiazem', 'verapamil', 'nicardipine', 'clevidipine', 'amlodipine', 'nifedipine'] },
  
  // Vasodilators
  'vasodilator': { displayName: 'Vasodilators', drugs: ['nitroprusside', 'nitroglycerin', 'hydralazine', 'nicardipine', 'clevidipine'] },
  'vasodilators': { displayName: 'Vasodilators', drugs: ['nitroprusside', 'nitroglycerin', 'hydralazine', 'nicardipine', 'clevidipine'] },
  
  // Vasopressors / Pressors
  'vasopressor': { displayName: 'Vasopressors', drugs: ['norepinephrine', 'phenylephrine', 'vasopressin', 'epinephrine', 'dopamine'] },
  'vasopressors': { displayName: 'Vasopressors', drugs: ['norepinephrine', 'phenylephrine', 'vasopressin', 'epinephrine', 'dopamine'] },
  'pressor': { displayName: 'Vasopressors', drugs: ['norepinephrine', 'phenylephrine', 'vasopressin', 'epinephrine', 'dopamine'] },
  'pressors': { displayName: 'Vasopressors', drugs: ['norepinephrine', 'phenylephrine', 'vasopressin', 'epinephrine', 'dopamine'] },
  
  // Inotropes
  'inotrope': { displayName: 'Inotropes', drugs: ['dobutamine', 'milrinone', 'dopamine', 'epinephrine'] },
  'inotropes': { displayName: 'Inotropes', drugs: ['dobutamine', 'milrinone', 'dopamine', 'epinephrine'] },
  
  // Opioids / Pain management
  'opioid': { displayName: 'Opioids', drugs: ['fentanyl', 'morphine', 'hydromorphone'] },
  'opioids': { displayName: 'Opioids', drugs: ['fentanyl', 'morphine', 'hydromorphone'] },
  'opiate': { displayName: 'Opioids', drugs: ['fentanyl', 'morphine', 'hydromorphone'] },
  'opiates': { displayName: 'Opioids', drugs: ['fentanyl', 'morphine', 'hydromorphone'] },
  'narcotic': { displayName: 'Opioids', drugs: ['fentanyl', 'morphine', 'hydromorphone'] },
  'narcotics': { displayName: 'Opioids', drugs: ['fentanyl', 'morphine', 'hydromorphone'] },
  'painmanagement': { displayName: 'Pain Management', drugs: ['fentanyl', 'morphine', 'hydromorphone', 'ketamine'] },
  
  // Benzodiazepines
  'benzodiazepine': { displayName: 'Benzodiazepines', drugs: ['midazolam', 'lorazepam', 'diazepam'] },
  'benzodiazepines': { displayName: 'Benzodiazepines', drugs: ['midazolam', 'lorazepam', 'diazepam'] },
  'benzo': { displayName: 'Benzodiazepines', drugs: ['midazolam', 'lorazepam', 'diazepam'] },
  'benzos': { displayName: 'Benzodiazepines', drugs: ['midazolam', 'lorazepam', 'diazepam'] },
  
  // Sedatives
  'sedative': { displayName: 'Sedatives', drugs: ['propofol', 'midazolam', 'ketamine', 'etomidate'] },
  'sedatives': { displayName: 'Sedatives', drugs: ['propofol', 'midazolam', 'ketamine', 'etomidate'] },
  'sedation': { displayName: 'Sedatives', drugs: ['propofol', 'midazolam', 'ketamine', 'etomidate'] },
  
  // Paralytics / Neuromuscular blockers
  'paralytic': { displayName: 'Paralytics', drugs: ['rocuronium', 'succinylcholine', 'vecuronium', 'cisatracurium'] },
  'paralytics': { displayName: 'Paralytics', drugs: ['rocuronium', 'succinylcholine', 'vecuronium', 'cisatracurium'] },
  'neuromuscularblocking': { displayName: 'Neuromuscular Blockers', drugs: ['rocuronium', 'succinylcholine', 'vecuronium', 'cisatracurium'] },
  'nmb': { displayName: 'Neuromuscular Blockers', drugs: ['rocuronium', 'succinylcholine', 'vecuronium', 'cisatracurium'] },
  
  // Anticoagulants
  'anticoagulant': { displayName: 'Anticoagulants', drugs: ['heparin', 'enoxaparin', 'apixaban', 'rivaroxaban', 'warfarin', 'dabigatran'] },
  'anticoagulants': { displayName: 'Anticoagulants', drugs: ['heparin', 'enoxaparin', 'apixaban', 'rivaroxaban', 'warfarin', 'dabigatran'] },
  'bloodthinner': { displayName: 'Anticoagulants', drugs: ['heparin', 'enoxaparin', 'apixaban', 'rivaroxaban', 'warfarin', 'dabigatran'] },
  'bloodthinners': { displayName: 'Anticoagulants', drugs: ['heparin', 'enoxaparin', 'apixaban', 'rivaroxaban', 'warfarin', 'dabigatran'] },
  
  // Thrombolytics
  'thrombolytic': { displayName: 'Thrombolytics', drugs: ['alteplase', 'tenecteplase', 'reteplase'] },
  'thrombolytics': { displayName: 'Thrombolytics', drugs: ['alteplase', 'tenecteplase', 'reteplase'] },
  'tpa': { displayName: 'Thrombolytics', drugs: ['alteplase', 'tenecteplase'] },
  
  // Antiarrhythmics
  'antiarrhythmic': { displayName: 'Antiarrhythmics', drugs: ['amiodarone', 'lidocaine', 'procainamide', 'adenosine', 'diltiazem'] },
  'antiarrhythmics': { displayName: 'Antiarrhythmics', drugs: ['amiodarone', 'lidocaine', 'procainamide', 'adenosine', 'diltiazem'] },
  
  // Antibiotics
  'antibiotic': { displayName: 'Antibiotics', drugs: ['vancomycin', 'ceftriaxone', 'piperacillintazobactam', 'meropenem', 'azithromycin'] },
  'antibiotics': { displayName: 'Antibiotics', drugs: ['vancomycin', 'ceftriaxone', 'piperacillintazobactam', 'meropenem', 'azithromycin'] },
  
  // Diuretics
  'diuretic': { displayName: 'Diuretics', drugs: ['furosemide', 'hydrochlorothiazide', 'spironolactone', 'chlorthalidone'] },
  'diuretics': { displayName: 'Diuretics', drugs: ['furosemide', 'hydrochlorothiazide', 'spironolactone', 'chlorthalidone'] },
  
  // ACE inhibitors
  'aceinhibitor': { displayName: 'ACE Inhibitors', drugs: ['lisinopril', 'enalapril', 'captopril', 'enalaprilat'] },
  'aceinhibitors': { displayName: 'ACE Inhibitors', drugs: ['lisinopril', 'enalapril', 'captopril', 'enalaprilat'] },
  'acei': { displayName: 'ACE Inhibitors', drugs: ['lisinopril', 'enalapril', 'captopril', 'enalaprilat'] },
  
  // ARBs
  'arb': { displayName: 'ARBs', drugs: ['losartan', 'valsartan'] },
  'arbs': { displayName: 'ARBs', drugs: ['losartan', 'valsartan'] },
  'angiotensinreceptorblocker': { displayName: 'ARBs', drugs: ['losartan', 'valsartan'] },
  
  // Antiepileptics / Anticonvulsants
  'antiepileptic': { displayName: 'Antiepileptics', drugs: ['levetiracetam', 'fosphenytoin', 'valproicacid', 'phenobarbital', 'lacosamide'] },
  'antiepileptics': { displayName: 'Antiepileptics', drugs: ['levetiracetam', 'fosphenytoin', 'valproicacid', 'phenobarbital', 'lacosamide'] },
  'anticonvulsant': { displayName: 'Anticonvulsants', drugs: ['levetiracetam', 'fosphenytoin', 'valproicacid', 'phenobarbital', 'lacosamide'] },
  'anticonvulsants': { displayName: 'Anticonvulsants', drugs: ['levetiracetam', 'fosphenytoin', 'valproicacid', 'phenobarbital', 'lacosamide'] },
  
  // Reversal agents
  'reversalagent': { displayName: 'Reversal Agents', drugs: ['naloxone', 'flumazenil', 'idarucizumab', 'andexanetalfa', 'vitamink'] },
  'reversalagents': { displayName: 'Reversal Agents', drugs: ['naloxone', 'flumazenil', 'idarucizumab', 'andexanetalfa', 'vitamink'] },
  'antidote': { displayName: 'Antidotes', drugs: ['naloxone', 'flumazenil', 'nacetylcysteine', 'fomepizole', 'digoxinimmunefab'] },
  'antidotes': { displayName: 'Antidotes', drugs: ['naloxone', 'flumazenil', 'nacetylcysteine', 'fomepizole', 'digoxinimmunefab'] },
};

// Drug index: normalized drug name -> array of {file, displayName}
// displayName is the name as it appears in the sheet
const DRUG_INDEX = {
  // Antiarrhythmics
  'diltiazem': [{ file: 'antiarrhythmics.html', displayName: 'Diltiazem' }, { file: 'emergency-med-dosing.html', displayName: 'Diltiazem' }],
  'verapamil': [{ file: 'antiarrhythmics.html', displayName: 'Verapamil' }],
  'metoprolol': [{ file: 'antiarrhythmics.html', displayName: 'Metoprolol' }, { file: 'antihypertensive-dosing.html', displayName: 'Metoprolol Tartrate' }],
  'metoprololtartrate': [{ file: 'antihypertensive-dosing.html', displayName: 'Metoprolol Tartrate' }],
  'metoprololsuccinate': [{ file: 'antihypertensive-dosing.html', displayName: 'Metoprolol Succinate' }],
  'esmolol': [{ file: 'antiarrhythmics.html', displayName: 'Esmolol' }, { file: 'antihypertensive-dosing.html', displayName: 'Esmolol' }, { file: 'emergency-med-dosing.html', displayName: 'Esmolol' }],
  'propranolol': [{ file: 'antiarrhythmics.html', displayName: 'Propranolol' }],
  'digoxin': [{ file: 'antiarrhythmics.html', displayName: 'Digoxin' }],
  'procainamide': [{ file: 'antiarrhythmics.html', displayName: 'Procainamide' }],
  'lidocaine': [{ file: 'antiarrhythmics.html', displayName: 'Lidocaine' }, { file: 'emergency-med-dosing.html', displayName: 'Lidocaine' }],
  'flecainide': [{ file: 'antiarrhythmics.html', displayName: 'Flecainide' }],
  'propafenone': [{ file: 'antiarrhythmics.html', displayName: 'Propafenone' }],
  'amiodarone': [{ file: 'antiarrhythmics.html', displayName: 'Amiodarone' }, { file: 'emergency-med-dosing.html', displayName: 'Amiodarone' }],
  'sotalol': [{ file: 'antiarrhythmics.html', displayName: 'Sotalol' }],
  'dofetilide': [{ file: 'antiarrhythmics.html', displayName: 'Dofetilide' }],
  'ibutilide': [{ file: 'antiarrhythmics.html', displayName: 'Ibutilide' }],
  'adenosine': [{ file: 'antiarrhythmics.html', displayName: 'Adenosine' }, { file: 'emergency-med-dosing.html', displayName: 'Adenosine' }],
  'magnesiumsulfate': [{ file: 'antiarrhythmics.html', displayName: 'Magnesium Sulfate' }, { file: 'emergency-med-dosing.html', displayName: 'Magnesium' }],
  'magnesium': [{ file: 'antiarrhythmics.html', displayName: 'Magnesium Sulfate' }, { file: 'emergency-med-dosing.html', displayName: 'Magnesium' }],

  // Antidotes & Reversal
  'naloxone': [{ file: 'antidotes-reversal.html', displayName: 'Naloxone' }, { file: 'emergency-med-dosing.html', displayName: 'Naloxone' }, { file: 'toxicology-protocols.html', displayName: 'Naloxone' }],
  'flumazenil': [{ file: 'antidotes-reversal.html', displayName: 'Flumazenil' }, { file: 'toxicology-protocols.html', displayName: 'Flumazenil' }],
  'vitamink': [{ file: 'antidotes-reversal.html', displayName: 'Vitamin K' }],
  'phytonadione': [{ file: 'antidotes-reversal.html', displayName: 'Vitamin K' }],
  'prothrombincomplexconcentrate': [{ file: 'antidotes-reversal.html', displayName: 'Prothrombin Complex Concentrate (PCC)' }],
  'pcc': [{ file: 'antidotes-reversal.html', displayName: 'Prothrombin Complex Concentrate (PCC)' }],
  'freshfrozenplasma': [{ file: 'antidotes-reversal.html', displayName: 'Fresh Frozen Plasma (FFP)' }],
  'ffp': [{ file: 'antidotes-reversal.html', displayName: 'Fresh Frozen Plasma (FFP)' }],
  'idarucizumab': [{ file: 'antidotes-reversal.html', displayName: 'Idarucizumab' }],
  'andexanetalfa': [{ file: 'antidotes-reversal.html', displayName: 'Andexanet Alfa' }],
  'protaminesulfate': [{ file: 'antidotes-reversal.html', displayName: 'Protamine Sulfate' }],
  'protamine': [{ file: 'antidotes-reversal.html', displayName: 'Protamine Sulfate' }],
  'nacetylcysteine': [{ file: 'antidotes-reversal.html', displayName: 'N-Acetylcysteine (NAC)' }, { file: 'toxicology-protocols.html', displayName: 'N-Acetylcysteine' }],
  'nac': [{ file: 'antidotes-reversal.html', displayName: 'N-Acetylcysteine (NAC)' }, { file: 'toxicology-protocols.html', displayName: 'N-Acetylcysteine' }],
  'acetylcysteine': [{ file: 'antidotes-reversal.html', displayName: 'N-Acetylcysteine (NAC)' }, { file: 'toxicology-protocols.html', displayName: 'N-Acetylcysteine' }],
  'fomepizole': [{ file: 'antidotes-reversal.html', displayName: 'Fomepizole' }, { file: 'toxicology-protocols.html', displayName: 'Fomepizole' }],
  'ethanol': [{ file: 'antidotes-reversal.html', displayName: 'Ethanol' }, { file: 'toxicology-protocols.html', displayName: 'Ethanol' }],
  'thiamine': [{ file: 'antidotes-reversal.html', displayName: 'Thiamine' }],
  'folate': [{ file: 'antidotes-reversal.html', displayName: 'Folate' }],
  'folicacid': [{ file: 'antidotes-reversal.html', displayName: 'Folate' }],
  'calciumchloride': [{ file: 'antidotes-reversal.html', displayName: 'Calcium Chloride' }, { file: 'toxicology-protocols.html', displayName: 'Calcium Chloride' }],
  'calciumgluconate': [{ file: 'antidotes-reversal.html', displayName: 'Calcium Gluconate' }, { file: 'toxicology-protocols.html', displayName: 'Calcium Gluconate' }],
  'highdoseinsulin': [{ file: 'antidotes-reversal.html', displayName: 'High-Dose Insulin' }, { file: 'toxicology-protocols.html', displayName: 'High-Dose Insulin' }],
  'insulin': [{ file: 'antidotes-reversal.html', displayName: 'High-Dose Insulin' }, { file: 'toxicology-protocols.html', displayName: 'High-Dose Insulin' }],
  'glucagon': [{ file: 'antidotes-reversal.html', displayName: 'Glucagon' }, { file: 'toxicology-protocols.html', displayName: 'Glucagon' }],
  'digoxinimmunefab': [{ file: 'antidotes-reversal.html', displayName: 'Digoxin Immune Fab' }, { file: 'toxicology-protocols.html', displayName: 'Digoxin Immune Fab' }],
  'digifab': [{ file: 'antidotes-reversal.html', displayName: 'Digoxin Immune Fab' }, { file: 'toxicology-protocols.html', displayName: 'Digoxin Immune Fab' }],
  'sodiumbicarbonate': [{ file: 'antidotes-reversal.html', displayName: 'Sodium Bicarbonate' }, { file: 'toxicology-protocols.html', displayName: 'Sodium Bicarbonate' }],
  'lipidemulsion': [{ file: 'antidotes-reversal.html', displayName: 'Lipid Emulsion' }],
  'intralipid': [{ file: 'antidotes-reversal.html', displayName: 'Lipid Emulsion' }],
  'methyleneblue': [{ file: 'antidotes-reversal.html', displayName: 'Methylene Blue' }],
  'hydroxocobalamin': [{ file: 'antidotes-reversal.html', displayName: 'Hydroxocobalamin' }, { file: 'toxicology-protocols.html', displayName: 'Hydroxocobalamin' }],
  'cyanokit': [{ file: 'antidotes-reversal.html', displayName: 'Hydroxocobalamin' }, { file: 'toxicology-protocols.html', displayName: 'Hydroxocobalamin' }],
  'octreotide': [{ file: 'antidotes-reversal.html', displayName: 'Octreotide' }],
  'deferoxamine': [{ file: 'antidotes-reversal.html', displayName: 'Deferoxamine' }, { file: 'toxicology-protocols.html', displayName: 'Deferoxamine' }],

  // Antihypertensive Dosing
  'labetalol': [{ file: 'antihypertensive-dosing.html', displayName: 'Labetalol' }],
  'nicardipine': [{ file: 'antihypertensive-dosing.html', displayName: 'Nicardipine' }],
  'clevidipine': [{ file: 'antihypertensive-dosing.html', displayName: 'Clevidipine' }],
  'hydralazine': [{ file: 'antihypertensive-dosing.html', displayName: 'Hydralazine' }],
  'enalaprilat': [{ file: 'antihypertensive-dosing.html', displayName: 'Enalaprilat' }],
  'nitroprusside': [{ file: 'antihypertensive-dosing.html', displayName: 'Nitroprusside' }, { file: 'emergency-med-dosing.html', displayName: 'Nitroprusside' }],
  'nitroglycerin': [{ file: 'antihypertensive-dosing.html', displayName: 'Nitroglycerin' }, { file: 'emergency-med-dosing.html', displayName: 'Nitroglycerin' }],
  'phentolamine': [{ file: 'antihypertensive-dosing.html', displayName: 'Phentolamine' }],
  'lisinopril': [{ file: 'antihypertensive-dosing.html', displayName: 'Lisinopril' }],
  'enalapril': [{ file: 'antihypertensive-dosing.html', displayName: 'Enalapril' }],
  'captopril': [{ file: 'antihypertensive-dosing.html', displayName: 'Captopril' }],
  'losartan': [{ file: 'antihypertensive-dosing.html', displayName: 'Losartan' }],
  'valsartan': [{ file: 'antihypertensive-dosing.html', displayName: 'Valsartan' }],
  'carvedilol': [{ file: 'antihypertensive-dosing.html', displayName: 'Carvedilol' }],
  'amlodipine': [{ file: 'antihypertensive-dosing.html', displayName: 'Amlodipine' }],
  'nifedipine': [{ file: 'antihypertensive-dosing.html', displayName: 'Nifedipine' }],
  'hydrochlorothiazide': [{ file: 'antihypertensive-dosing.html', displayName: 'Hydrochlorothiazide' }],
  'hctz': [{ file: 'antihypertensive-dosing.html', displayName: 'Hydrochlorothiazide' }],
  'chlorthalidone': [{ file: 'antihypertensive-dosing.html', displayName: 'Chlorthalidone' }],
  'furosemide': [{ file: 'antihypertensive-dosing.html', displayName: 'Furosemide' }],
  'lasix': [{ file: 'antihypertensive-dosing.html', displayName: 'Furosemide' }],
  'spironolactone': [{ file: 'antihypertensive-dosing.html', displayName: 'Spironolactone' }],
  'aldactone': [{ file: 'antihypertensive-dosing.html', displayName: 'Spironolactone' }],
  'clonidine': [{ file: 'antihypertensive-dosing.html', displayName: 'Clonidine' }],
  'doxazosin': [{ file: 'antihypertensive-dosing.html', displayName: 'Doxazosin' }],
  'minoxidil': [{ file: 'antihypertensive-dosing.html', displayName: 'Minoxidil' }],

  // Emergency Med Dosing
  'atropine': [{ file: 'emergency-med-dosing.html', displayName: 'Atropine' }, { file: 'toxicology-protocols.html', displayName: 'Atropine' }],
  'epinephrine': [{ file: 'emergency-med-dosing.html', displayName: 'Epinephrine' }, { file: 'iv-pressors-dosing.html', displayName: 'Epinephrine' }],
  'adrenaline': [{ file: 'emergency-med-dosing.html', displayName: 'Epinephrine' }, { file: 'iv-pressors-dosing.html', displayName: 'Epinephrine' }],
  'vasopressin': [{ file: 'emergency-med-dosing.html', displayName: 'Vasopressin' }, { file: 'iv-pressors-dosing.html', displayName: 'Vasopressin' }],
  'etomidate': [{ file: 'emergency-med-dosing.html', displayName: 'Etomidate' }, { file: 'procedural-sedation.html', displayName: 'Etomidate' }],
  'midazolam': [{ file: 'emergency-med-dosing.html', displayName: 'Midazolam' }, { file: 'procedural-sedation.html', displayName: 'Midazolam' }, { file: 'status-epilepticus.html', displayName: 'Midazolam' }],
  'versed': [{ file: 'emergency-med-dosing.html', displayName: 'Midazolam' }, { file: 'procedural-sedation.html', displayName: 'Midazolam' }, { file: 'status-epilepticus.html', displayName: 'Midazolam' }],
  'propofol': [{ file: 'emergency-med-dosing.html', displayName: 'Propofol' }, { file: 'procedural-sedation.html', displayName: 'Propofol' }],
  'diprivan': [{ file: 'emergency-med-dosing.html', displayName: 'Propofol' }, { file: 'procedural-sedation.html', displayName: 'Propofol' }],
  'ketamine': [{ file: 'emergency-med-dosing.html', displayName: 'Ketamine' }, { file: 'procedural-sedation.html', displayName: 'Ketamine' }, { file: 'status-epilepticus.html', displayName: 'Ketamine' }],
  'rocuronium': [{ file: 'emergency-med-dosing.html', displayName: 'Rocuronium' }],
  'succinylcholine': [{ file: 'emergency-med-dosing.html', displayName: 'Succinylcholine' }],
  'suxamethonium': [{ file: 'emergency-med-dosing.html', displayName: 'Succinylcholine' }],
  'fentanyl': [{ file: 'emergency-med-dosing.html', displayName: 'Fentanyl' }, { file: 'procedural-sedation.html', displayName: 'Fentanyl' }],
  'vecuronium': [{ file: 'emergency-med-dosing.html', displayName: 'Vecuronium' }],
  'cisatracurium': [{ file: 'emergency-med-dosing.html', displayName: 'Cisatracurium' }],
  'dobutamine': [{ file: 'emergency-med-dosing.html', displayName: 'Dobutamine' }, { file: 'iv-pressors-dosing.html', displayName: 'Dobutamine' }],
  'dopamine': [{ file: 'emergency-med-dosing.html', displayName: 'Dopamine' }, { file: 'iv-pressors-dosing.html', displayName: 'Dopamine' }],
  'isoproterenol': [{ file: 'emergency-med-dosing.html', displayName: 'Isoproterenol' }, { file: 'iv-pressors-dosing.html', displayName: 'Isoproterenol' }],
  'milrinone': [{ file: 'emergency-med-dosing.html', displayName: 'Milrinone' }, { file: 'iv-pressors-dosing.html', displayName: 'Milrinone' }],
  'norepinephrine': [{ file: 'emergency-med-dosing.html', displayName: 'Norepinephrine' }, { file: 'iv-pressors-dosing.html', displayName: 'Norepinephrine' }],
  'levophed': [{ file: 'emergency-med-dosing.html', displayName: 'Norepinephrine' }, { file: 'iv-pressors-dosing.html', displayName: 'Norepinephrine' }],
  'phenylephrine': [{ file: 'emergency-med-dosing.html', displayName: 'Phenylephrine' }, { file: 'iv-pressors-dosing.html', displayName: 'Phenylephrine' }],
  'neosynephrine': [{ file: 'emergency-med-dosing.html', displayName: 'Phenylephrine' }, { file: 'iv-pressors-dosing.html', displayName: 'Phenylephrine' }],

  // Empiric Antibiotics
  'vancomycin': [{ file: 'empiric-antibiotics.html', displayName: 'Vancomycin' }],
  'piperacillintazobactam': [{ file: 'empiric-antibiotics.html', displayName: 'Piperacillin-Tazobactam' }],
  'zosyn': [{ file: 'empiric-antibiotics.html', displayName: 'Piperacillin-Tazobactam' }],
  'meropenem': [{ file: 'empiric-antibiotics.html', displayName: 'Meropenem' }],
  'cefepime': [{ file: 'empiric-antibiotics.html', displayName: 'Cefepime' }],
  'maxipime': [{ file: 'empiric-antibiotics.html', displayName: 'Cefepime' }],
  'aztreonam': [{ file: 'empiric-antibiotics.html', displayName: 'Aztreonam' }],
  'metronidazole': [{ file: 'empiric-antibiotics.html', displayName: 'Metronidazole' }],
  'flagyl': [{ file: 'empiric-antibiotics.html', displayName: 'Metronidazole' }],
  'ceftriaxone': [{ file: 'empiric-antibiotics.html', displayName: 'Ceftriaxone' }],
  'rocephin': [{ file: 'empiric-antibiotics.html', displayName: 'Ceftriaxone' }],
  'azithromycin': [{ file: 'empiric-antibiotics.html', displayName: 'Azithromycin' }],
  'zithromax': [{ file: 'empiric-antibiotics.html', displayName: 'Azithromycin' }],
  'zpak': [{ file: 'empiric-antibiotics.html', displayName: 'Azithromycin' }],
  'levofloxacin': [{ file: 'empiric-antibiotics.html', displayName: 'Levofloxacin' }],
  'levaquin': [{ file: 'empiric-antibiotics.html', displayName: 'Levofloxacin' }],
  'moxifloxacin': [{ file: 'empiric-antibiotics.html', displayName: 'Moxifloxacin' }],
  'avelox': [{ file: 'empiric-antibiotics.html', displayName: 'Moxifloxacin' }],
  'doxycycline': [{ file: 'empiric-antibiotics.html', displayName: 'Doxycycline' }],
  'amoxicillin': [{ file: 'empiric-antibiotics.html', displayName: 'Amoxicillin' }],
  'ampicillin': [{ file: 'empiric-antibiotics.html', displayName: 'Ampicillin' }],
  'ciprofloxacin': [{ file: 'empiric-antibiotics.html', displayName: 'Ciprofloxacin' }],
  'cipro': [{ file: 'empiric-antibiotics.html', displayName: 'Ciprofloxacin' }],
  'nitrofurantoin': [{ file: 'empiric-antibiotics.html', displayName: 'Nitrofurantoin' }],
  'macrobid': [{ file: 'empiric-antibiotics.html', displayName: 'Nitrofurantoin' }],
  'trimethoprimsulfamethoxazole': [{ file: 'empiric-antibiotics.html', displayName: 'Trimethoprim-Sulfamethoxazole' }],
  'bactrim': [{ file: 'empiric-antibiotics.html', displayName: 'Trimethoprim-Sulfamethoxazole' }],
  'tmpsmx': [{ file: 'empiric-antibiotics.html', displayName: 'Trimethoprim-Sulfamethoxazole' }],
  'fosfomycin': [{ file: 'empiric-antibiotics.html', displayName: 'Fosfomycin' }],
  'ertapenem': [{ file: 'empiric-antibiotics.html', displayName: 'Ertapenem' }],
  'invanz': [{ file: 'empiric-antibiotics.html', displayName: 'Ertapenem' }],
  'cefazolin': [{ file: 'empiric-antibiotics.html', displayName: 'Cefazolin' }],
  'ancef': [{ file: 'empiric-antibiotics.html', displayName: 'Cefazolin' }],
  'cephalexin': [{ file: 'empiric-antibiotics.html', displayName: 'Cephalexin' }],
  'keflex': [{ file: 'empiric-antibiotics.html', displayName: 'Cephalexin' }],
  'clindamycin': [{ file: 'empiric-antibiotics.html', displayName: 'Clindamycin' }],
  'linezolid': [{ file: 'empiric-antibiotics.html', displayName: 'Linezolid' }],
  'zyvox': [{ file: 'empiric-antibiotics.html', displayName: 'Linezolid' }],
  'amoxicillinclavulanate': [{ file: 'empiric-antibiotics.html', displayName: 'Amoxicillin-Clavulanate' }],
  'augmentin': [{ file: 'empiric-antibiotics.html', displayName: 'Amoxicillin-Clavulanate' }],
  'ampicillinsulbactam': [{ file: 'empiric-antibiotics.html', displayName: 'Ampicillin-Sulbactam' }],
  'unasyn': [{ file: 'empiric-antibiotics.html', displayName: 'Ampicillin-Sulbactam' }],
  'gentamicin': [{ file: 'empiric-antibiotics.html', displayName: 'Gentamicin' }],
  'cefotaxime': [{ file: 'empiric-antibiotics.html', displayName: 'Cefotaxime' }],
  'cefoxitin': [{ file: 'empiric-antibiotics.html', displayName: 'Cefoxitin' }],
  'cefpodoxime': [{ file: 'empiric-antibiotics.html', displayName: 'Cefpodoxime' }],
  'acyclovir': [{ file: 'empiric-antibiotics.html', displayName: 'Acyclovir' }],
  'zovirax': [{ file: 'empiric-antibiotics.html', displayName: 'Acyclovir' }],

  // IV Pressors Dosing
  'ephedrine': [{ file: 'iv-pressors-dosing.html', displayName: 'Ephedrine' }],

  // Procedural Sedation
  'methohexital': [{ file: 'procedural-sedation.html', displayName: 'Methohexital' }],
  'brevital': [{ file: 'procedural-sedation.html', displayName: 'Methohexital' }],
  'lorazepam': [{ file: 'procedural-sedation.html', displayName: 'Lorazepam' }, { file: 'status-epilepticus.html', displayName: 'Lorazepam' }],
  'ativan': [{ file: 'procedural-sedation.html', displayName: 'Lorazepam' }, { file: 'status-epilepticus.html', displayName: 'Lorazepam' }],
  'morphine': [{ file: 'procedural-sedation.html', displayName: 'Morphine' }],
  'hydromorphone': [{ file: 'procedural-sedation.html', displayName: 'Hydromorphone' }],
  'dilaudid': [{ file: 'procedural-sedation.html', displayName: 'Hydromorphone' }],
  'nitrousoxide': [{ file: 'procedural-sedation.html', displayName: 'Nitrous Oxide' }],

  // Status Epilepticus
  'diazepam': [{ file: 'status-epilepticus.html', displayName: 'Diazepam' }],
  'valium': [{ file: 'status-epilepticus.html', displayName: 'Diazepam' }],
  'levetiracetam': [{ file: 'status-epilepticus.html', displayName: 'Levetiracetam' }],
  'keppra': [{ file: 'status-epilepticus.html', displayName: 'Levetiracetam' }],
  'fosphenytoin': [{ file: 'status-epilepticus.html', displayName: 'Fosphenytoin' }],
  'cerebyx': [{ file: 'status-epilepticus.html', displayName: 'Fosphenytoin' }],
  'phenytoin': [{ file: 'status-epilepticus.html', displayName: 'Fosphenytoin' }],
  'dilantin': [{ file: 'status-epilepticus.html', displayName: 'Fosphenytoin' }],
  'valproicacid': [{ file: 'status-epilepticus.html', displayName: 'Valproic Acid' }],
  'depakote': [{ file: 'status-epilepticus.html', displayName: 'Valproic Acid' }],
  'phenobarbital': [{ file: 'status-epilepticus.html', displayName: 'Phenobarbital' }],
  'pentobarbital': [{ file: 'status-epilepticus.html', displayName: 'Pentobarbital' }],
  'lacosamide': [{ file: 'status-epilepticus.html', displayName: 'Lacosamide' }],
  'vimpat': [{ file: 'status-epilepticus.html', displayName: 'Lacosamide' }],
  'brivaracetam': [{ file: 'status-epilepticus.html', displayName: 'Brivaracetam' }],
  'topiramate': [{ file: 'status-epilepticus.html', displayName: 'Topiramate' }],
  'topamax': [{ file: 'status-epilepticus.html', displayName: 'Topiramate' }],

  // Thrombolytics & Anticoag
  'alteplase': [{ file: 'thrombolytics-anticoag.html', displayName: 'Alteplase (tPA)' }],
  'tpa': [{ file: 'thrombolytics-anticoag.html', displayName: 'Alteplase (tPA)' }],
  'activase': [{ file: 'thrombolytics-anticoag.html', displayName: 'Alteplase (tPA)' }],
  'tenecteplase': [{ file: 'thrombolytics-anticoag.html', displayName: 'Tenecteplase' }],
  'tnkase': [{ file: 'thrombolytics-anticoag.html', displayName: 'Tenecteplase' }],
  'reteplase': [{ file: 'thrombolytics-anticoag.html', displayName: 'Reteplase' }],
  'retavase': [{ file: 'thrombolytics-anticoag.html', displayName: 'Reteplase' }],
  'heparin': [{ file: 'thrombolytics-anticoag.html', displayName: 'Heparin (UFH)' }],
  'ufh': [{ file: 'thrombolytics-anticoag.html', displayName: 'Heparin (UFH)' }],
  'enoxaparin': [{ file: 'thrombolytics-anticoag.html', displayName: 'Enoxaparin' }],
  'lovenox': [{ file: 'thrombolytics-anticoag.html', displayName: 'Enoxaparin' }],
  'dalteparin': [{ file: 'thrombolytics-anticoag.html', displayName: 'Dalteparin' }],
  'fragmin': [{ file: 'thrombolytics-anticoag.html', displayName: 'Dalteparin' }],
  'apixaban': [{ file: 'thrombolytics-anticoag.html', displayName: 'Apixaban' }],
  'eliquis': [{ file: 'thrombolytics-anticoag.html', displayName: 'Apixaban' }],
  'rivaroxaban': [{ file: 'thrombolytics-anticoag.html', displayName: 'Rivaroxaban' }],
  'xarelto': [{ file: 'thrombolytics-anticoag.html', displayName: 'Rivaroxaban' }],
  'edoxaban': [{ file: 'thrombolytics-anticoag.html', displayName: 'Edoxaban' }],
  'savaysa': [{ file: 'thrombolytics-anticoag.html', displayName: 'Edoxaban' }],
  'dabigatran': [{ file: 'thrombolytics-anticoag.html', displayName: 'Dabigatran' }],
  'pradaxa': [{ file: 'thrombolytics-anticoag.html', displayName: 'Dabigatran' }],
  'warfarin': [{ file: 'thrombolytics-anticoag.html', displayName: 'Warfarin' }],
  'coumadin': [{ file: 'thrombolytics-anticoag.html', displayName: 'Warfarin' }],

  // Toxicology Protocols
  'pralidoxime': [{ file: 'toxicology-protocols.html', displayName: 'Pralidoxime' }],
  '2pam': [{ file: 'toxicology-protocols.html', displayName: 'Pralidoxime' }],
};

/**
 * Normalize drug name for lookup
 * Removes route suffixes, salt forms, and special characters
 * @param {string} name - Drug name to normalize
 * @returns {string} Normalized drug name
 */
export const normalizeDrugName = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/\s*(iv|po|im|sc|sl|pr|td|oral|injection)\s*$/i, '') // Remove route suffixes
    .replace(/\s*(tartrate|succinate|sulfate|chloride|gluconate|sodium|potassium|hydrochloride|hcl)\s*$/i, '') // Remove salt forms
    .replace(/[^a-z0-9]/g, '') // Remove special characters, spaces, hyphens
    .trim();
};

/**
 * Extract actual drug name from treatment name
 * Handles formats like "Category (e.g., DrugName)" or "Category (DrugName)"
 * @param {string} treatmentName - Full treatment name
 * @returns {string[]} Array of possible drug names to try (most specific first)
 */
export const extractDrugNames = (treatmentName) => {
  if (!treatmentName) return [];
  
  const candidates = [];
  
  // Try to extract from "e.g., DrugName" or "eg, DrugName" pattern
  const egMatch = treatmentName.match(/e\.?g\.?,?\s*([A-Za-z][A-Za-z0-9\s-]*)/i);
  if (egMatch) {
    candidates.push(egMatch[1].trim());
  }
  
  // Try to extract from parentheses "(DrugName)"
  const parenMatch = treatmentName.match(/\(([A-Za-z][A-Za-z0-9\s-]*)\)/);
  if (parenMatch && !parenMatch[1].toLowerCase().startsWith('e.g')) {
    candidates.push(parenMatch[1].trim());
  }
  
  // Try the full name as-is
  candidates.push(treatmentName);
  
  // Try the first word if it looks like a drug name (capitalized, not a generic category)
  const firstWord = treatmentName.split(/[\s(]/)[0];
  if (firstWord && firstWord.length > 3 && /^[A-Z]/.test(firstWord)) {
    candidates.push(firstWord);
  }
  
  return candidates;
};

/**
 * Look up drug category in the DRUG_CATEGORIES index
 * @param {string} categoryName - Category name like "beta-blocker"
 * @returns {Object|null} Category info { displayName, drugs } or null if not found
 */
const lookupCategory = (categoryName) => {
  if (!categoryName) return null;
  const normalized = normalizeDrugName(categoryName);
  return DRUG_CATEGORIES[normalized] || null;
};

/**
 * Check if searched name differs from matched name
 * Returns true if there's a notable difference the user should see
 * @param {string} searchedName - Original name searched
 * @param {string} matchedName - Name as it appears in the sheet
 * @returns {boolean} True if names differ
 */
export const checkNameDifference = (searchedName, matchedName) => {
  if (!searchedName || !matchedName) return false;
  
  // Exact match (case-insensitive) = no difference
  if (searchedName.toLowerCase().trim() === matchedName.toLowerCase().trim()) {
    return false;
  }
  
  // Otherwise, show the difference
  return true;
};

/**
 * Look up drug in the index
 * Tries multiple extraction strategies for names like "Category (e.g., DrugName)"
 * Also checks drug categories like "beta-blocker" to find relevant medsheets
 * @param {string} drugName - Name to look up
 * @returns {Object} { sheets: [{file, title, displayName}], hasNameDifference: boolean, searchedName: string, matchedName: string, isCategory: boolean }
 */
export const lookupDrug = (drugName) => {
  if (!drugName) {
    return { sheets: [], hasNameDifference: false, searchedName: '', matchedName: '', isCategory: false };
  }

  // Extract possible drug name candidates from the treatment name
  const candidates = extractDrugNames(drugName);
  
  // Try each candidate until we find a match in the drug index
  for (const candidate of candidates) {
    const normalizedName = normalizeDrugName(candidate);
    const entries = DRUG_INDEX[normalizedName];
    
    if (entries && entries.length > 0) {
      // Found a match!
      const matchedName = entries[0].displayName;
      // Name difference if the original treatment name doesn't exactly match the display name
      const hasNameDifference = checkNameDifference(drugName, matchedName);

      // Map entries to include title
      const sheets = entries.map(entry => ({
        file: entry.file,
        title: SHEET_TITLES[entry.file] || entry.file,
        displayName: entry.displayName,
      }));

      return {
        sheets,
        hasNameDifference,
        searchedName: drugName,
        matchedName,
        isCategory: false,
      };
    }
  }
  
  // No direct drug match found - try category lookup
  for (const candidate of candidates) {
    const categoryInfo = lookupCategory(candidate);
    
    if (categoryInfo) {
      // Found a category match! Collect sheets from all drugs in this category
      const sheetsMap = new Map(); // Use map to dedupe by file
      
      for (const drugKey of categoryInfo.drugs) {
        const entries = DRUG_INDEX[drugKey];
        if (entries) {
          for (const entry of entries) {
            if (!sheetsMap.has(entry.file)) {
              sheetsMap.set(entry.file, {
                file: entry.file,
                title: SHEET_TITLES[entry.file] || entry.file,
                displayName: categoryInfo.displayName, // Show category name instead of individual drug
              });
            }
          }
        }
      }
      
      if (sheetsMap.size > 0) {
        return {
          sheets: Array.from(sheetsMap.values()),
          hasNameDifference: true, // Category is always a "name difference"
          searchedName: drugName,
          matchedName: categoryInfo.displayName,
          isCategory: true,
        };
      }
    }
  }
  
  // No match found
  return { sheets: [], hasNameDifference: false, searchedName: drugName, matchedName: '', isCategory: false };
};

/**
 * Quick check if a drug has any medsheets
 * Tries multiple extraction strategies for names like "Category (e.g., DrugName)"
 * Also checks drug categories like "beta-blocker"
 * @param {string} drugName - Name to check
 * @returns {boolean} True if drug has medsheets
 */
export const hasMedsheet = (drugName) => {
  if (!drugName) return false;
  
  // Extract possible drug name candidates
  const candidates = extractDrugNames(drugName);
  
  
  // Try each candidate - check both drug index and categories
  for (const candidate of candidates) {
    const normalizedName = normalizeDrugName(candidate);
    
    
    // Check direct drug match
    if (DRUG_INDEX[normalizedName]) {
      return true;
    }
    
    // Check category match
    if (DRUG_CATEGORIES[normalizedName]) {
      return true;
    }
  }
  
  return false;
};

/**
 * Get URL for a medsheet file
 * @param {string} sheetFile - Filename of the sheet
 * @returns {string} URL path to the sheet
 */
export const getSheetUrl = (sheetFile) => {
  // Medsheets are in the public folder at /medsheets/
  return `/medsheets/${sheetFile}`;
};

/**
 * Get all available sheet titles (for debugging/admin)
 * @returns {Object} Map of filename to title
 */
export const getAllSheetTitles = () => {
  return { ...SHEET_TITLES };
};
